import { FROG_X, GROUND, HEAD_H } from '../../sim/constants';
import { drawReeds, drawWater, ell, hash01, hills, skyGradient } from '../common';
import { smallPads } from './pond';
import type { DrawCtx, SceneArt } from './types';

const FIREFLIES = 16;

function firefly(i: number, t: number): { x: number; y: number; glow: number } {
  const x = 400 + Math.sin(t * 0.23 + i * 1.7) * 330 + Math.sin(t * 0.61 + i) * 40;
  const y = 270 + Math.sin(t * 0.37 + i * 2.3) * 95 + Math.cos(t * 0.9 + i * 0.7) * 18;
  const glow = 0.55 + 0.45 * Math.sin(t * 2.4 + i * 1.3);
  return { x, y, glow };
}

let dark: HTMLCanvasElement | null = null;

/** Night: a dark layer with holes where fireflies, the moon and the frog give light. */
function darkness(d: DrawCtx): void {
  const { v } = d;
  const w = Math.max(1, Math.round(v.cw * v.dpr));
  const h = Math.max(1, Math.round(v.ch * v.dpr));
  if (!dark) dark = document.createElement('canvas');
  if (dark.width !== w || dark.height !== h) {
    dark.width = w;
    dark.height = h;
  }
  const c = dark.getContext('2d');
  if (!c) return;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.globalCompositeOperation = 'source-over';
  c.clearRect(0, 0, w, h);
  c.fillStyle = `rgba(3,8,22,${d.hc ? 0.3 : 0.66})`;
  c.fillRect(0, 0, w, h);
  c.globalCompositeOperation = 'destination-out';
  c.setTransform(v.dpr * v.s, 0, 0, v.dpr * v.s, v.dpr * v.ox, v.dpr * v.oy);
  const hole = (x: number, y: number, r: number, a = 1) => {
    const g = c.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(0,0,0,${a})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    c.fillStyle = g;
    c.fillRect(x - r, y - r, r * 2, r * 2);
  };
  hole(230, 230, 190, 0.9);
  for (let i = 0; i < FIREFLIES; i++) {
    const f = firefly(i, d.t);
    hole(f.x, f.y, 90, f.glow);
  }
  // the frog's glow, and a pulse on the pad each time the rope passes
  hole(FROG_X, GROUND - d.frogY - HEAD_H, 120, 0.85);
  hole(FROG_X, GROUND - 10, 60 + 110 * d.beat, 0.4 + 0.6 * d.beat);
  d.ctx.save();
  d.ctx.setTransform(1, 0, 0, 1, 0, 0);
  d.ctx.drawImage(dark, 0, 0);
  d.ctx.restore();
}

export const swamp: SceneArt = {
  id: 'swamp',
  rope: '#F2C14E',
  ropeBack: '#B98C2A',
  handle: '#F2C14E',
  ring: 'rgba(255,226,120,0.85)',
  ground: {
    waterTop: '#1B4250', waterBottom: '#0A1E28', shimmer: 'rgba(200,220,255,0.16)',
    padTop: '#3D7F4A', padEdge: '#1B4A2A', padVein: 'rgba(10,40,20,0.45)',
    post: '#5E4330', postDark: '#45301F', postCap: '#7F6045', ripple: 'rgba(220,235,255,0.45)',
  },
  drawBack(d: DrawCtx) {
    const { ctx, v, t } = d;
    skyGradient(ctx, v, [[0, '#0B1530'], [0.7, '#182A4C'], [1, '#2A4066']]);
    for (let i = 0; i < 60; i++) {
      const x = v.L + hash01(i) * (v.R - v.L);
      const y = v.T + hash01(i + 100) * (330 - v.T);
      const tw = d.reduced ? 0.7 : 0.4 + 0.6 * Math.abs(Math.sin(t * 1.5 + i));
      ctx.globalAlpha = tw;
      ell(ctx, x, y, 1.3, 1.3);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    const glow = ctx.createRadialGradient(230, 230, 8, 230, 230, 90);
    glow.addColorStop(0, 'rgba(240,240,210,0.6)');
    glow.addColorStop(1, 'rgba(240,240,210,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(140, 140, 180, 180);
    ell(ctx, 230, 230, 28, 28);
    ctx.fillStyle = '#F4F1D0';
    ctx.fill();
    hills(ctx, v.L, v.R, 350, 30, 0.014, 2.2, '#16283E');
    // cypress silhouettes
    ctx.fillStyle = '#0F1F2E';
    for (let i = 0; i < 9; i++) {
      const x = v.L + (i + 0.5) * ((v.R - v.L) / 9) + Math.sin(i * 3) * 20;
      const h = 60 + hash01(i + 40) * 50;
      ctx.beginPath();
      ctx.moveTo(x - 14, 362);
      ctx.quadraticCurveTo(x - 6, 362 - h * 0.5, x, 362 - h);
      ctx.quadraticCurveTo(x + 6, 362 - h * 0.5, x + 14, 362);
      ctx.fill();
    }
    hills(ctx, v.L, v.R, 364, 12, 0.025, 0.3, '#0E1C28');
    drawWater(ctx, v, t, this.ground, 0.8);
    ctx.strokeStyle = 'rgba(240,240,200,0.35)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const half = 18 - i * 3 + Math.sin(t * 1.6 + i) * 3;
      ctx.beginPath();
      ctx.moveTo(230 - half, 370 + i * 7);
      ctx.lineTo(230 + half, 370 + i * 7);
      ctx.stroke();
    }
    smallPads(ctx, '#24563A');
    drawReeds(ctx, 30, 3, t, '#21402C', '#3B2A1E', 3);
    drawReeds(ctx, 762, 4, t, '#21402C', '#3B2A1E', 3);
    // fireflies
    for (let i = 0; i < FIREFLIES; i++) {
      const f = firefly(i, t);
      const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, 10);
      g.addColorStop(0, `rgba(255,240,140,${0.9 * f.glow})`);
      g.addColorStop(1, 'rgba(255,240,140,0)');
      ctx.fillStyle = g;
      ctx.fillRect(f.x - 10, f.y - 10, 20, 20);
    }
  },
  drawFront(d: DrawCtx) {
    darkness(d);
  },
};
